import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { spanish } from '../../api';
import { playSpanish } from '../../utils/spanishAudio';
import './Spanish.css';

const STATUS_LABEL = { new: 'New', learning: 'Learning', known: 'Learned', mastered: 'Mastered' };
const MEETUP = 'meetup';

const fmtWhen = (d) => new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * Coach / headcoach view of one student's Spanish progress. Two tracks:
 *
 *  1. SESSION WORDS - the coach picks the words that belong to a lesson / meetup, the student is told
 *     what they heard when it ends, and the coach APPROVES each word (Learned / Not yet).
 *  2. EVERYTHING ELSE - generic words progress automatically through the student's own practice.
 *     The coach can see it but has nothing to approve.
 *
 * props: playerId, playerName, reviewLogId (set when opened from the "Spanish check" push)
 */
export default function CoachSpanishPanel({ playerId, playerName, reviewLogId }) {
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState({ bookings: [], logs: [] });
  const [exams, setExams] = useState([]);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]);       // words chosen for the session
  const [sessionKey, setSessionKey] = useState(MEETUP); // a booking id, or MEETUP (right now)
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [marks, setMarks] = useState({});         // itemId -> learned | not_yet

  const load = useCallback(async () => {
    try {
      const [tree, sess, ex] = await Promise.all([spanish.student(playerId), spanish.sessions(playerId).catch(() => null), spanish.exams(playerId).catch(() => null)]);
      setData(tree.data);
      if (sess) setSessions(sess.data);
      if (ex) setExams(ex.data.exams || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load Spanish progress.');
    }
  }, [playerId]);

  useEffect(() => { setData(null); setPicked([]); setMarks({}); setMsg(''); load(); }, [load]);

  // Default to the next lesson with this student; preload words already picked for it
  useEffect(() => {
    if (!sessions.bookings.length) return;
    const now = Date.now();
    const next = sessions.bookings.find(b => new Date(b.end).getTime() > now) || sessions.bookings[sessions.bookings.length - 1];
    setSessionKey(prev => (prev === MEETUP ? next._id : prev));
  }, [sessions.bookings]);

  useEffect(() => {
    const log = sessions.logs.find(l => l.bookingId && String(l.bookingId) === sessionKey && l.planned);
    setPicked(log ? log.itemIds.map(String) : []);
    setNote(log ? log.message || '' : '');
  }, [sessionKey, sessions.logs]);

  const allItems = useMemo(() => (data ? data.units.flatMap(u => u.items.map(i => ({ ...i, unitTitle: u.title, unitIcon: u.icon }))) : []), [data]);
  const awaiting = useMemo(() => allItems.filter(i => i.awaitingApproval), [allItems]);
  const byId = useMemo(() => new Map(allItems.map(i => [String(i._id), i])), [allItems]);

  const togglePick = (id) => setPicked(p => (p.includes(String(id)) ? p.filter(x => x !== String(id)) : [...p, String(id)]));

  const sendSession = async () => {
    if (picked.length === 0) return;
    setBusy(true);
    setMsg('');
    try {
      const res = await spanish.planSession(playerId, { itemIds: picked, bookingId: sessionKey === MEETUP ? null : sessionKey, message: note.trim() || undefined });
      setData(d => ({ ...d, units: res.data.units, courses: res.data.courses, summary: res.data.summary }));
      setMsg(res.data.sentNow
        ? `Sent - ${playerName} has been told these ${res.data.words} word${res.data.words !== 1 ? 's' : ''}.`
        : `Saved - ${playerName} will get these words when the lesson ends, and you'll be asked to approve them.`);
      const sess = await spanish.sessions(playerId);
      setSessions(sess.data);
      if (sessionKey === MEETUP) setPicked([]);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Could not save the session words.');
    } finally {
      setBusy(false);
    }
  };

  const submitApproval = async () => {
    const reviews = Object.entries(marks).map(([itemId, status]) => ({ itemId, status }));
    if (reviews.length === 0) return;
    setBusy(true);
    setMsg('');
    try {
      const res = await spanish.review(playerId, reviews, reviewLogId || undefined);
      setData(d => ({ ...d, units: res.data.units, courses: res.data.courses, summary: res.data.summary }));
      setMarks({});
      setMsg(`Saved - ${res.data.learned} word${res.data.learned !== 1 ? 's' : ''} approved as learned.`);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="sp-error">{error}</div>;
  if (!data) return <div className="sp-empty">Loading…</div>;

  if (!data.student.enabled) {
    return (
      <div className="sp-card sp-empty">
        🇪🇸 {playerName} hasn't turned on Padel Español.<br />
        They can enable it in Settings → Spanish learning.
      </div>
    );
  }

  const { units, summary } = data;
  const courses = data.courses && data.courses.length ? data.courses : [{ n: 1, title: 'Spanish' }];
  const heroCourse = courses.find(x => !x.locked && !x.passed) || courses[courses.length - 1];
  const q = search.trim().toLowerCase();
  const found = q ? allItems.filter(i => i.es.toLowerCase().includes(q) || i.en.toLowerCase().includes(q)).slice(0, 12) : [];
  const markCount = Object.keys(marks).length;

  const wordRow = (it) => (
    <div className="sp-row" key={it._id}>
      <input type="checkbox" checked={picked.includes(String(it._id))} aria-label={`Pick ${it.es} for the session`} onChange={() => togglePick(it._id)} />
      <div className="sp-phrase-main">
        <div className="sp-es" style={{ fontSize: 15 }}>{it.es} {it.assigned && it.awaitingApproval && <span className="sp-hw" title="Picked for a session, waiting for your approval">🎯</span>}</div>
        <div className="sp-en">{it.en}</div>
      </div>
      <span className={`sp-chip sp-chip--${it.status}`}>{STATUS_LABEL[it.status]}</span>
      <button className="sp-play" onClick={() => playSpanish(it)} aria-label={`Listen to ${it.es}`}>🔊</button>
    </div>
  );

  return (
    <div className="sp-wrap">
      <div className="sp-hero">
        <h2>🇪🇸 {playerName}</h2>
        <div className="sp-hero-stats">
          <div className="sp-stat"><b>{summary.currentLevel}</b><span>Level</span></div>
          <div className="sp-stat"><b>{summary.known}/{summary.total}</b><span>Learned</span></div>
          <div className="sp-stat"><b>{summary.streak}🔥</b><span>Streak</span></div>
          <div className="sp-stat"><b>{summary.xp}</b><span>XP</span></div>
        </div>
        {heroCourse && (
          <>
            <div className="sp-level-bar"><i style={{ width: `${heroCourse.passed ? 100 : Math.min(100, Math.round((heroCourse.studied / Math.max(1, heroCourse.minStudied)) * 100))}%` }} /></div>
            <div className="sp-level-label">
              {summary.allPassed ? 'Every level completed'
                : heroCourse.passed ? `Level ${heroCourse.n} exam passed · best ${heroCourse.bestPercent}%`
                : heroCourse.examReady ? `Level ${heroCourse.n} exam is open to them`
                : `Studied ${heroCourse.studied}/${heroCourse.minStudied} Level ${heroCourse.n} words (exam opens at ${heroCourse.minStudied})`}
            </div>
          </>
        )}
      </div>

      {msg && <div className="sp-feedback sp-feedback--ok" style={{ margin: 0 }}>{msg}</div>}

      {/* 1a. approve session words */}
      <div className={`sp-card ${awaiting.length ? 'sp-today' : ''}`}>
        <h3>✅ Approve session words</h3>
        {awaiting.length === 0 ? (
          <p className="sp-sub" style={{ margin: 0 }}>Nothing waiting. Words you pick for a session show up here for your approval.</p>
        ) : (
          <>
            <p className="sp-sub">Has {playerName.split(' ')[0]} learned these? Only you can mark session words as learned.</p>
            {awaiting.map(it => (
              <div className="sp-row" key={it._id}>
                <div className="sp-phrase-main">
                  <div className="sp-es" style={{ fontSize: 16 }}>{it.es}</div>
                  <div className="sp-en">{it.en}</div>
                </div>
                <button className="sp-play" onClick={() => playSpanish(it)} aria-label={`Listen to ${it.es}`}>🔊</button>
                <div className="sp-seg" role="group" aria-label={`Approval for ${it.es}`}>
                  <button className={marks[it._id] === 'learned' ? 'on-ok' : ''} onClick={() => setMarks(m => ({ ...m, [it._id]: 'learned' }))}>Learned</button>
                  <button className={marks[it._id] === 'not_yet' ? 'on-no' : ''} onClick={() => setMarks(m => ({ ...m, [it._id]: 'not_yet' }))}>Not yet</button>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 10 }}>
              <button className="sp-btn" onClick={submitApproval} disabled={busy || markCount === 0}>Save {markCount > 0 ? `(${markCount})` : ''}</button>
            </div>
          </>
        )}
      </div>

      {/* 1b. pick words for a session */}
      <div className="sp-card">
        <h3>🎯 Words for a session</h3>
        <p className="sp-sub">Pick the words and phrases that came up (or will come up) in the lesson. {playerName.split(' ')[0]} is told when it ends, and you approve them above.</p>

        <label className="sp-field-label" htmlFor="sp-session">Session</label>
        <select id="sp-session" className="sp-select" value={sessionKey} onChange={(e) => setSessionKey(e.target.value)}>
          {sessions.bookings.map(b => (
            <option key={b._id} value={b._id}>{fmtWhen(b.start)}{b.title ? ` · ${b.title}` : ''}</option>
          ))}
          <option value={MEETUP}>Meetup / not booked - send now</option>
        </select>

        <label className="sp-field-label" htmlFor="sp-search">Find a word</label>
        <input id="sp-search" className="sp-input" type="search" placeholder="Search Spanish or English…" value={search} onChange={(e) => setSearch(e.target.value)} />
        {found.length > 0 && (
          <div className="sp-found">
            {found.map(it => (
              <label key={it._id} className="sp-found-row">
                <input type="checkbox" checked={picked.includes(String(it._id))} onChange={() => togglePick(it._id)} />
                <span><b>{it.es}</b> <span className="sp-en">- {it.en}</span> <span className="sp-sub" style={{ margin: 0 }}>({it.unitIcon} {it.unitTitle})</span></span>
              </label>
            ))}
          </div>
        )}
        {q && found.length === 0 && <div className="sp-sub">No matching words.</div>}

        {picked.length > 0 && (
          <div className="sp-picked">
            {picked.map(id => {
              const it = byId.get(id);
              return it ? (
                <button key={id} type="button" className="sp-pick-chip" onClick={() => togglePick(id)} aria-label={`Remove ${it.es}`}>{it.es} ✕</button>
              ) : null;
            })}
          </div>
        )}

        <input className="sp-input" type="text" maxLength={80} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional message to the student" style={{ marginTop: 8 }} />
        <div style={{ marginTop: 10 }}>
          <button className="sp-btn" onClick={sendSession} disabled={busy || picked.length === 0}>
            {sessionKey === MEETUP ? '📨 Send now' : '💾 Save for this lesson'} {picked.length > 0 ? `(${picked.length})` : ''}
          </button>
        </div>
      </div>

      {/* exams */}
      <div className="sp-card">
        <h3>📝 Exams</h3>
        {exams.length === 0 ? (
          <p className="sp-sub" style={{ margin: 0 }}>No exams taken yet. {playerName.split(' ')[0]} can sit the Level 1 exam after studying {courses[0] && courses[0].minStudied} words; passing opens Level 2.</p>
        ) : (
          exams.map(e => (
            <div className="sp-row" key={e._id}>
              <div className="sp-phrase-main">
                <div className="sp-es" style={{ fontSize: 15 }}>Level {e.level} · {e.percent}% <span className="sp-sub" style={{ margin: 0 }}>({e.correct}/{e.total})</span></div>
                <div className="sp-ex">{fmtWhen(e.at)}{e.missed.length ? ` · missed: ${e.missed.map(m => m.es).join(', ')}` : ''}</div>
              </div>
              <span className={`sp-chip sp-chip--${e.passed ? 'known' : 'learning'}`}>{e.passed ? 'Passed' : 'Not passed'}</span>
            </div>
          ))
        )}
      </div>

      {/* 2. generic progress is automatic */}
      <div className="sp-card">
        <h3>📈 Automatic progress</h3>
        <p className="sp-sub" style={{ margin: 0 }}>
          All other words progress on their own as {playerName.split(' ')[0]} practises. You don't need to approve these - tick any word below to add it to a session.
        </p>
      </div>

      {courses.map(course => (
        <section key={course.n} className="sp-course">
          <div className="sp-course-head">
            <h3>{course.title}</h3>
            {course.total > 0 && <span className="sp-course-count">{course.known}/{course.total}{course.locked ? ' · locked for student' : ''}</span>}
          </div>
          <div className="sp-units">
            {units.filter(u => (u.course || 1) === course.n).map(u => (
              <div className="sp-card" key={u.key}>
                <button className="sp-unit-head" onClick={() => setOpen(o => ({ ...o, [u.key]: !o[u.key] }))} aria-expanded={!!open[u.key]}>
                  <span className="sp-unit-icon">{u.icon}</span>
                  <span style={{ flex: 1 }}>
                    <div className="sp-unit-title">{u.title}</div>
                    <div className="sp-unit-meta">{u.known}/{u.total} learned</div>
                    <div className="sp-bar"><i style={{ width: `${u.percent}%` }} /></div>
                  </span>
                  <span>{open[u.key] ? '▲' : '▼'}</span>
                </button>
                {open[u.key] && <div className="sp-unit-body">{u.items.map(wordRow)}</div>}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
